import { DotsVertical } from "@untitledui/icons";
import type { FC, ReactNode, RefAttributes } from "react";
import type {
	ButtonProps as AriaButtonProps,
	MenuItemProps as AriaMenuItemProps,
	MenuProps as AriaMenuProps,
	PopoverProps as AriaPopoverProps,
	SeparatorProps as AriaSeparatorProps,
} from "react-aria-components";
import {
	Button as AriaButton,
	Header as AriaHeader,
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	MenuSection as AriaMenuSection,
	MenuTrigger as AriaMenuTrigger,
	Popover as AriaPopover,
	Separator as AriaSeparator,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";

/** Figma DropdownSectionHeader Order (3412:24764). */
export const DropdownSectionHeaderOrder = {
	Top: "top",
	Below: "below",
	Search: "search",
} as const;

export type DropdownSectionHeaderOrder =
	(typeof DropdownSectionHeaderOrder)[keyof typeof DropdownSectionHeaderOrder];

export interface DropdownSectionHeaderProps {
	/** Section title (Figma Text). Prefer over `children`. */
	title?: string;
	/** Fallback title content when `title` is omitted (legacy). */
	children?: ReactNode;
	/**
	 * Figma Order variant.
	 * - `search` — display title for Fixed search (text-lg, 28px, no separator)
	 * - `top` — uppercase xs section label (36px, no separator)
	 * - `below` — uppercase xs label with separator above (76px)
	 * @default "below"
	 */
	order?: DropdownSectionHeaderOrder;
	/** Show trailing action button (Figma Show action). @default false */
	showAction?: boolean;
	/** Trailing action label. @default "Reset" */
	actionLabel?: string;
	/** Called when the trailing action is pressed. */
	onAction?: () => void;
	/** Accessible name for the trailing action when it needs a scoped label. */
	actionAriaLabel?: string;
	/** Test id for the trailing action button. */
	actionTestId?: string;
	className?: string;
}

/**
 * Figma DropdownSectionHeader (3412:24764) — section label with optional
 * trailing Reset (Link sm).
 *
 * Order:
 * - `search` — display title (text-lg / 28px), Fixed search blocks
 * - `top` — uppercase xs label (36px), first section in a menu
 * - `below` — uppercase xs label + top separator (76px)
 *
 * @see https://www.figma.com/design/L1OY04yBGp8MljRalQdcuK/Web-DesignSystem-2026?node-id=3412-24764
 */
export function DropdownSectionHeader({
	title,
	children,
	order = DropdownSectionHeaderOrder.Below,
	showAction = false,
	actionLabel = "Reset",
	onAction,
	actionAriaLabel,
	actionTestId,
	className,
}: DropdownSectionHeaderProps) {
	const label = title ?? children;
	const isTop = order === DropdownSectionHeaderOrder.Top;
	const isSearch = order === DropdownSectionHeaderOrder.Search;
	const showSeparator = order === DropdownSectionHeaderOrder.Below;
	/** Only Search uses the display title; Top + Below use uppercase section labels. */
	const isDisplayTitle = isSearch;

	const titleRow = (
		<div
			className={cx(
				"flex w-full items-center justify-between gap-(--spacing-xl,16px)",
				// Figma symbol heights: Search 28 / Top 36.
				isSearch && "min-h-7",
				isTop && "min-h-9",
			)}
		>
			<span
				className={cx(
					"min-w-0 flex-1 text-left font-display font-extrabold",
					isDisplayTitle
						? "text-lg leading-7 text-primary"
						: "text-xs leading-4 tracking-[0.24px] text-quaternary uppercase",
				)}
			>
				{label}
			</span>
			{showAction ? (
				<Button
					data-testid={actionTestId}
					size="sm"
					color="link-gray"
					className="shrink-0 text-sm font-semibold"
					aria-label={actionAriaLabel}
					onClick={onAction}
				>
					{actionLabel}
				</Button>
			) : null}
		</div>
	);

	return (
		<AriaHeader
			data-testid="dropdown-section-header"
			data-order={order}
			className={cx(
				"flex w-full flex-col",
				// Popover already applies spacing-xl — headers stay flush horizontally
				// so title text left-aligns with the Search display title / menu edge.
				showSeparator
					? "gap-(--spacing-xl,16px) px-0 py-(--spacing-xl,16px)"
					: isSearch
						? "px-0 py-0"
						: "px-0 py-0",
				className,
			)}
		>
			{showSeparator ? (
				<hr className="h-px w-full shrink-0 border-0 bg-border-secondary" />
			) : null}
			{titleRow}
		</AriaHeader>
	);
}

interface DropdownItemProps extends AriaMenuItemProps {
	/** The label of the item to be displayed. */
	label?: string;
	/** An addon to be displayed on the right side of the item. */
	addon?: string;
	/** If true, the item will not have any styles. */
	unstyled?: boolean;
	/** An icon to be displayed on the left side of the item. */
	icon?: FC<{ className?: string }>;
}

const DropdownItem = ({
	label,
	children,
	addon,
	icon: Icon,
	unstyled,
	...props
}: DropdownItemProps) => {
	if (unstyled) {
		return (
			<AriaMenuItem id={label} textValue={label} {...props}>
				{children}
			</AriaMenuItem>
		);
	}

	return (
		<AriaMenuItem
			{...props}
			className={(state) =>
				cx(
					// No outer inset — Dropdown.Popover owns spacing-xl padding alone.
					"group block cursor-pointer outline-hidden",
					state.isDisabled && "cursor-not-allowed",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			{(state) => (
				<div
					className={cx(
						"relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear",
						!state.isDisabled && "group-hover:bg-primary_hover",
						state.isFocused && "bg-primary_hover",
						state.isFocusVisible && "outline-2 -outline-offset-2",
					)}
				>
					{Icon && (
						<Icon
							aria-hidden="true"
							className={cx(
								"mr-2 size-4 shrink-0 stroke-[2.25px]",
								state.isDisabled ? "text-fg-disabled" : "text-fg-quaternary",
							)}
						/>
					)}

					<span
						className={cx(
							"grow truncate text-sm font-semibold",
							state.isDisabled ? "text-disabled" : "text-secondary",
							state.isFocused && "text-secondary_hover",
						)}
					>
						{label ||
							(typeof children === "function" ? children(state) : children)}
					</span>

					{addon && (
						<span
							className={cx(
								"ml-3 shrink-0 rounded px-1 py-px text-xs font-medium ring-1 ring-secondary ring-inset",
								state.isDisabled ? "text-disabled" : "text-quaternary",
							)}
						>
							{addon}
						</span>
					)}
				</div>
			)}
		</AriaMenuItem>
	);
};

interface DropdownMenuProps<T extends object> extends AriaMenuProps<T> {}

const DropdownMenu = <T extends object>(props: DropdownMenuProps<T>) => {
	return (
		<AriaMenu
			disallowEmptySelection
			selectionMode="single"
			{...props}
			className={(state) =>
				cx(
					// Popover owns spacing-xl padding — no extra menu py.
					"h-min overflow-y-auto py-0 outline-hidden select-none",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		/>
	);
};

interface DropdownPopoverProps extends AriaPopoverProps {}

const DropdownPopover = (props: DropdownPopoverProps) => {
	return (
		<AriaPopover
			placement="bottom right"
			{...props}
			className={(state) =>
				cx(
					// Figma DropdownMenu container — radius-2xl, spacing-xl padding.
					"w-62 origin-(--trigger-anchor-point) overflow-auto rounded-2xl bg-primary p-(--spacing-xl,16px) shadow-lg ring-1 ring-secondary_alt will-change-transform",
					state.isEntering &&
						"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
					state.isExiting &&
						"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			{props.children}
		</AriaPopover>
	);
};

const DropdownSeparator = (props: AriaSeparatorProps) => {
	return (
		<AriaSeparator
			{...props}
			className={cx("my-1 h-px w-full bg-border-secondary", props.className)}
		/>
	);
};

const DropdownDotsButton = ({
	"aria-label": ariaLabel,
	...props
}: AriaButtonProps & RefAttributes<HTMLButtonElement>) => {
	return (
		<AriaButton
			{...props}
			aria-label={ariaLabel ?? "Open menu"}
			className={(state) =>
				cx(
					"cursor-pointer rounded-md text-fg-quaternary outline-focus-ring transition duration-100 ease-linear",
					(state.isPressed || state.isHovered) && "text-fg-quaternary_hover",
					(state.isPressed || state.isFocusVisible) &&
						"outline-2 outline-offset-2",
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			<DotsVertical className="size-5 transition-inherit-all" />
		</AriaButton>
	);
};

export const Dropdown = {
	Root: AriaMenuTrigger,
	Popover: DropdownPopover,
	Menu: DropdownMenu,
	Section: AriaMenuSection,
	SectionHeader: DropdownSectionHeader,
	Item: DropdownItem,
	Separator: DropdownSeparator,
	DotsButton: DropdownDotsButton,
};
